import { TokenIcon } from "~/components/token-icon";
import { cn } from "~/lib/utils";

interface StackedTokenIconsProps {
  className?: string;
  /** Token identities in their visible stack order. */
  tokens: ReadonlyArray<{ url: string; symbol: string }>;
  /** Visual height in pixels; each icon renders at 65% of it. */
  size: number;
  /** Overrides the rendered icon diameter while preserving the stack height. */
  iconSize?: number;
  /** Maximum rendered items, including the optional overflow count. */
  maxVisible?: number;
  /** Number of pixels each icon overlaps the preceding icon. */
  overlap?: number;
  overflowClassName?: string;
  ringClassName?: string;
  /** Adds a final +N circle when tokens exceed maxVisible. */
  showOverflowCount?: boolean;
  stackOrder?: "first-on-top" | "last-on-top";
}

/** Overlapping token identities with optional overflow disclosure. */
export function StackedTokenIcons({
  className,
  iconSize: iconSizeOverride,
  maxVisible = 4,
  overlap: overlapOverride,
  overflowClassName,
  ringClassName,
  showOverflowCount = false,
  size,
  stackOrder = "first-on-top",
  tokens,
}: StackedTokenIconsProps) {
  const shouldShowOverflow = showOverflowCount && tokens.length > maxVisible;
  const visibleTokenLimit = shouldShowOverflow
    ? Math.max(maxVisible - 1, 0)
    : maxVisible;
  const items = tokens.slice(0, visibleTokenLimit);
  const overflowCount = Math.max(tokens.length - items.length, 0);
  const visibleItemCount = items.length + (shouldShowOverflow ? 1 : 0);
  const iconSize = iconSizeOverride ?? Math.round(size * 0.65);
  const overlap = overlapOverride ?? Math.round(iconSize * 0.35);
  const totalWidth =
    visibleItemCount > 0
      ? iconSize + (visibleItemCount - 1) * (iconSize - overlap)
      : 0;

  const getItemStyle = (index: number) => ({
    left: index * (iconSize - overlap),
    width: iconSize,
    height: iconSize,
    zIndex:
      stackOrder === "first-on-top" ? visibleItemCount - index : index + 1,
  });

  return (
    <div
      className={cn("relative isolate flex shrink-0 items-center", className)}
      style={{ width: totalWidth, height: size }}
    >
      {items.map((token, index) => (
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 rounded-full ring-2",
            ringClassName ?? "ring-background",
          )}
          key={`${token.symbol}-${index}`}
          style={getItemStyle(index)}
        >
          <TokenIcon
            alt={token.symbol}
            disableLink
            hideChainBadge
            size={iconSize}
            src={token.url}
          />
        </div>
      ))}
      {shouldShowOverflow ? (
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-1/2 grid -translate-y-1/2 place-items-center rounded-full bg-background/70 text-caption leading-none tabular-nums text-foreground ring-2 ring-background backdrop-blur-md",
            overflowClassName,
          )}
          style={{
            ...getItemStyle(items.length),
            zIndex: visibleItemCount + 1,
          }}
        >
          +{overflowCount}
        </span>
      ) : null}
    </div>
  );
}
